/**
 * Tâche 1246 (Eric 2026-06-28) — Carte « Scan des embeddings ».
 *
 * Le scan ne dépendait que d'un planificateur EXTERNE à brancher à la main :
 * s'il n'était jamais configuré, l'index de recherche pouvait se dégrader
 * pendant des semaines sans que personne ne le voie. Le planificateur interne
 * tourne chaque jour à 07:00 heure de l'Est ; cette carte le prouve à l'écran.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Database, Play, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export const EmbeddingScanCard = ({ initial , awaiting }) => {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/sentinel/embeddings/status`, {
        withCredentials: true,
      });
      setData(r.data);
    } catch (e) {
      if ([401, 403].includes(e?.response?.status)) setHidden(true);
    }
  }, []);

    // Tâche 1249 — Si la page a déjà tout reçu dans l'appel groupé, la carte
  // ne refait PAS son propre appel : la page passait 23 appels par affichage.
  useEffect(() => {
    if (initial) {
      setData(initial);
      return;
    }
    // Tâche 1249 — L'appel groupé est en route : ne pas en lancer un second.
    if (awaiting) return;
    load();
  }, [load, initial, awaiting]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await axios.post(
        `${API}/api/sentinel/embeddings/run-now`,
        {},
        { withCredentials: true }
      );
      setData(r.data.status);
    } catch (e) {
      if ([401, 403].includes(e?.response?.status)) setHidden(true);
    } finally {
      setRunning(false);
    }
  };

  if (hidden) return null;

  const missing = data?.missing;
  const degraded = typeof missing === "number" && missing > 0;
  const stale = data && !data.ran_today;

  return (
    <section
      className="rounded-2xl border border-zinc-200 bg-white p-5"
      data-testid="embedding-scan-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-zinc-900 flex items-center gap-2">
            <Database className="w-4 h-4 text-[#002FA7]" />
            Scan des embeddings
          </h2>
          <p className="text-sm text-zinc-500 mt-0.5">
            Chaque jour à {data?.scheduled_hour_local || "07:00 (heure de l'Est)"} —
            planificateur interne. L'appel externe reste accepté en filet de sécurité.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="p-2 rounded-lg border border-zinc-200 hover:bg-zinc-50"
            title="Rafraîchir"
            data-testid="embedding-scan-refresh"
          >
            <RefreshCw className="w-4 h-4 text-zinc-500" />
          </button>
          <button
            onClick={runNow}
            disabled={running}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002389] disabled:opacity-50"
            data-testid="embedding-scan-run-now"
          >
            <Play className="w-4 h-4" />
            {running ? "Scan en cours…" : "Lancer maintenant"}
          </button>
        </div>
      </div>

      {!data ? (
        <p className="mt-4 text-sm text-zinc-500">Chargement…</p>
      ) : (
        <>
          <p
            className={`mt-4 text-sm font-semibold ${
              degraded ? "text-amber-800" : "text-emerald-700"
            }`}
            data-testid="embedding-scan-verdict"
          >
            {degraded
              ? `${missing} élément(s) approuvé(s) sans index de recherche valide`
              : "Index de recherche complet au dernier scan"}
            {typeof data.total_approved === "number" && (
              <span className="font-normal text-zinc-500">
                {" "}
                · {data.total_approved} approuvé(s) au total
              </span>
            )}
          </p>

          <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-wide text-zinc-500">
                Dernier scan
              </dt>
              <dd
                className="font-semibold text-zinc-900"
                data-testid="embedding-scan-last-run"
              >
                {data.last_run_at
                  ? data.last_run_at.slice(0, 16).replace("T", " ")
                  : "jamais"}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-zinc-500">
                Déclenché par
              </dt>
              <dd className="font-semibold text-zinc-900">
                {data.last_source || "—"}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-zinc-500">
                Aujourd'hui
              </dt>
              <dd
                className={`font-semibold ${
                  stale ? "text-amber-700" : "text-emerald-700"
                }`}
                data-testid="embedding-scan-ran-today"
              >
                {data.ran_today ? "déjà passé" : "pas encore passé"}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-zinc-500">
                Prochain scan
              </dt>
              <dd
                className="font-semibold text-zinc-900"
                data-testid="embedding-scan-next-run"
              >
                {data.next_run_local}
              </dd>
            </div>
          </dl>

          {data.alerted && (
            <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Une alerte a été envoyée aux destinataires de la famille
              « Index de recherche dégradé ».
            </p>
          )}
        </>
      )}
    </section>
  );
};

export default EmbeddingScanCard;
